import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Select } from "@activecollab/components";
import PropTypes from "prop-types";
import Label from "~/records/record.label.jsx";
import { addLabel, addLabelToProject } from "~/store/actions/actions.labels.jsx";
import { makeTaskLabelsByProjectSelector } from "~/store/selectors/selector.labels.jsx";

export const SelectTaskLabels = ({ onChange, projectId, selected: defaultSelected, onClose, ...rest }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getTaskLabelsByProjectSelector = useMemo(makeTaskLabelsByProjectSelector, []);
  const labels = useSelector((state) => getTaskLabelsByProjectSelector(state, projectId));

  const handleAddLabel = useCallback(
    async (name) => {
      const newLabel = {
        name: name.toUpperCase(),
        type: `TaskLabel`,
        is_global: false,
        is_default: false,
        project_id: projectId,
      };

      const data = await dispatch(addLabel(newLabel));

      const { response = {} } = data;
      const { result = {}, entities } = response;
      const { labels } = entities;
      const { single: id } = result;

      return dispatch(addLabelToProject(projectId, new Label(labels[id])));
    },
    [dispatch, projectId]
  );

  const [selected, setSelected] = useState(defaultSelected);

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  const handleChange = useCallback(
    (labels) => {
      typeof onChange === "function" && onChange({ labels });
    },
    [onChange]
  );

  const handleOnAddNew = useCallback(
    (name) => {
      handleAddLabel(name)
        .then((data) => {
          const { response = {} } = data;
          const { result: id } = response;
          let nextState = [...selected, id];
          setSelected(nextState);

          nextState && typeof onChange === "function" && onChange({ labels: nextState });
        })
        .catch(() => {});
    },
    [handleAddLabel, onChange, selected]
  );

  const options = useMemo(() => {
    return labels.toArray().map((label) => {
      return {
        id: label.get("id"),
        name: label.get("name").toUpperCase(),
        color: label.get("color"),
        textColor: label.get("lighter_text_color"),
      };
    });
  }, [labels]);

  return (
    <Select
      {...rest}
      options={options}
      onChange={handleChange}
      type="multiple"
      emptyValue={t("Add new")}
      emptyAction={handleOnAddNew}
      selected={selected}
      autoHeightMax={278}
      onSelectClose={onClose}
    />
  );
};

SelectTaskLabels.propTypes = {
  projectId: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  onAddNew: PropTypes.func,
  onClose: PropTypes.func,
  selected: PropTypes.array,
};

SelectTaskLabels.defaultTypes = {
  selected: [],
};
